import Sidebar from "@/components/Common/Sidebar";
import { Outlet, useMatches } from "@tanstack/react-router";

import { useSidebar } from "@/hooks/use-sidebar";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "sonner";
import AdminNavbar from "@/features/admin/components/AdminNavbar";
import { useAutoRefreshToken } from "@/hooks/axios/useAutoRefreshToken";
import { AuthDebugPanel } from "@/components/Common/AuthDebugPanel";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 60 * 1000,
      gcTime: 60 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      enabled: true,
    },
    mutations: {
      retry: false,
    },
  },
});

function MainLayout() {
  const { isExpanded } = useSidebar();
  useAutoRefreshToken();

  const matches = useMatches();
  const layout =
    [...matches].reverse().find((m) => m.staticData?.layout)?.staticData
      ?.layout ?? "app";

  if (layout === "fullpage") {
    return <Outlet />;
  }

  if (layout === "auth") {
    return (
      <main className="bg-brand-50 flex flex-1 items-center justify-center">
        <Outlet />
      </main>
    );
  }

  if (layout === "admin") {
    return (
      <main className="bg-admin-bg flex h-screen text-white">
        <div className="grid min-h-0 flex-1">
          <div className="mx-auto flex min-h-0 w-full flex-1 flex-col gap-10">
            <AdminNavbar />
            <Outlet />
          </div>
        </div>
      </main>
    );
  }

  return (
    <div className="bg-brand-50 flex min-h-screen flex-row">
      <Sidebar isDarkMode={false} />
      <div
        className={`flex min-h-screen flex-1 flex-col ${
          isExpanded ? "ml-0" : "ml-0"
        }`}
      >
        <main className="flex-1">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export function RootLayout() {
  return (
    <QueryClientProvider client={queryClient}>
      <MainLayout />
      <Toaster position="bottom-right" />
      {/* Debug panel - set enabled={true} when debugging auth issues */}
      <AuthDebugPanel enabled={false} />
    </QueryClientProvider>
  );
}
